// src/services/agentAwareModelService.ts

import {
  ModelService,
  ModelRequest,
  ModelResponse,
  StreamCallbacks,
} from "../interfaces/modelService";

import { agentAwareApiAdapter } from "../api/agentAwareApiAdapter";
import { createXmlFile, generateDocumentId } from "../api/chatApi";
import { UnifiedChatRequest, UnifiedSummaryRequest } from "../interfaces/agentApiTypes";

// Import ChatStreamCallbacks from the correct location
import { ChatStreamCallbacks } from "../interfaces/chatTypes";
import { NormalizedCatylexSearchResult, CatylexToolInput } from "../interfaces/catylexSearchTypes";
import {
  NormalizedSharePointResult,
  SharePointToolInput,
} from "../interfaces/sharepointSearchTypes";
import { OutlookMailResult, OutlookMailToolInput } from "../interfaces/outlookMailTypes";
import { DocumentCitation } from "../interfaces/documentCitationTypes";
import { WebCitationInsertion } from "../interfaces/webCitationTypes";
import { ChatApiError } from "../api/chatApi";
import { createLogger } from "../utils/logger";
import { nullToUndefined } from "../utils/typeHelpers";

// Also need to define ChatMessage type locally or import it
interface ChatMessage {
  role: "user" | "assistant";
  content:
    | {
        text: string;
        thinking?: string;
      }
    | unknown[];
}
const log = createLogger("AgentAwareModelService");

/**
 * Agent-Aware Model Service
 *
 * This service extends the capabilities of the existing model service infrastructure
 * by adding agent awareness. It acts as a smart wrapper that knows which agent
 * is currently active and routes requests accordingly.
 *
 * Think of this like having a multilingual assistant - it speaks the same language
 * to your UI components (ModelService interface) but knows how to translate
 * that into different languages (APIs) based on which country (agent) you're in.
 *
 * Key design decisions:
 * 1. We don't modify the existing ChatModelService - we wrap it
 * 2. We maintain the same interface so UI components don't need to change
 * 3. We handle agent-specific logic (like additional files) transparently
 */
export class AgentAwareModelService implements ModelService {
  private agentId: string;
  private sessionId: string | null = null;
  private documentCache: Map<string, string> = new Map();

  // Set when a stored conversation is restored, cleared as soon as the first
  // request carries the notice. The model is told once that the conversation
  // was reopened, because the document now open may not be the one it was
  // originally about and nothing that was attached still is.
  private sessionResumePending = false;

  // Names of files the restored conversation was originally answered with. The
  // files are gone, so this is sent with the resume notice to let the model
  // account for them by name instead of appearing to have invented them.
  private sessionResumeAttachmentNames: string[] = [];

  // Track additional files for contract-qa agent
  private additionalFiles: File[] = [];

  // Track contract type from classification
  private contractType: string | null = null;

  // State tracking (similar to ChatModelService)
  private currentSessionFeatures = {
    hasThinkingEnabled: false,
    hasWebSearchEnabled: false,
    webSearchQueries: [] as string[],
    webSearchResults: [] as any[],
  };

  private currentThinkingState = {
    isThinking: false,
    thinkingStartTime: 0,
    thinkingEndTime: 0,
    accumulatedThinking: "",
  };

  constructor(agentId: string) {
    this.agentId = agentId;

    log.info("AgentAwareModelService initialized", {
      agentId,
      serviceType: "agent-aware",
    });
  }

  /**
   * Set additional files for contract-qa agent
   *
   * This method allows the UI to provide template files that will be
   * sent with requests when using the contract-qa agent.
   */
  setAdditionalFiles(files: File[]): void {
    this.additionalFiles = files;

    log.info("Additional files set", {
      agentId: this.agentId,
      fileCount: files.length,
      fileNames: files.map((f) => f.name),
    });
  }

  /**
   * Get current additional files
   */
  getAdditionalFiles(): File[] {
    return [...this.additionalFiles];
  }

  /**
   * Clear additional files
   * @deprecated Use clearFilesExplicit() instead. This method will be removed in future versions.
   *
   * Note: This method should only be called when explicitly requested by the user,
   * not as part of session management operations.
   */
  clearAdditionalFiles(): void {
    this.additionalFiles = [];
    log.warn("clearAdditionalFiles called - consider using clearFilesExplicit instead", {
      agentId: this.agentId,
      stackTrace: new Error().stack,
    });
  }

  /**
   * Clear files explicitly (not tied to session operations)
   * This should only be called when user explicitly wants to clear files
   */
  clearFilesExplicit(): void {
    const fileCount = this.additionalFiles.length;
    const fileNames = this.additionalFiles.map((f) => f.name);

    this.additionalFiles = [];

    log.info("Files explicitly cleared by user action", {
      agentId: this.agentId,
      clearedFileCount: fileCount,
      clearedFileNames: fileNames,
    });
  }

  /**
   * Check if this service has files
   */
  hasFiles(): boolean {
    return this.additionalFiles.length > 0;
  }

  /**
   * Get file count
   */
  getFileCount(): number {
    return this.additionalFiles.length;
  }

  /**
   * Get the current session ID
   */
  getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Continue a conversation that was restored from storage
   *
   * The stored backend session ID is injected so the next request extends the
   * same backend session instead of opening a new one, which is what keeps the
   * usage reports counting one conversation rather than one per reopen. It is
   * null when the conversation was stored before the agent ever sent a
   * request; the resume notice is still armed in that case.
   *
   * @param backendSessionId The stored session ID, or null if there was none
   * @param attachmentNames Files the conversation was answered with, which are
   *   no longer attached and cannot be restored
   */
  restoreSession(backendSessionId: string | null, attachmentNames: string[] = []): void {
    this.sessionId = backendSessionId;
    this.sessionResumePending = true;
    this.sessionResumeAttachmentNames = attachmentNames;

    // Seed the adapter too. It is the map the persistence layer reads when it
    // writes a session, and it is otherwise not populated until this agent
    // sends its first request - so a save in between would store a null id.
    agentAwareApiAdapter.adoptAgentSession(this.agentId, backendSessionId);

    log.info("Restored session for agent", {
      agentId: this.agentId,
      hasBackendSessionId: !!backendSessionId,
    });
  }

  /**
   * Set contract type from classification
   */
  setContractType(contractType: string | null): void {
    this.contractType = contractType;
    log.info("Contract type set", {
      agentId: this.agentId,
      contractType: contractType,
    });
  }

  /**
   * Get current contract type
   */
  getContractType(): string | null {
    return this.contractType;
  }

  /**
   * Generate streaming response - the main entry point
   *
   * This method maintains the same interface as ChatModelService but
   * routes requests through our agent-aware adapter.
   */
  async generateStreamingResponse(
    request: ModelRequest,
    callbacks: StreamCallbacks
  ): Promise<void> {
    try {
      log.info("Processing agent-aware model request", {
        agentId: this.agentId,
        extendedThinking: request.preferences?.extendedThinking || false,
        hasDocumentContext: !!request.documentContext,
        hasAdditionalFiles: this.additionalFiles.length > 0,
        conversationLength: request.conversationHistory?.length || 0,
      });

      // Transform to unified request format
      const unifiedRequest = await this.transformToUnifiedRequest(request);

      // Create callbacks that bridge between our adapter and the UI
      const adapterCallbacks = this.createCallbackBridge(callbacks, request.preferences);

      log.debug("Service routing request", {
        serviceAgent: this.agentId,
        hasAdditionalFiles: unifiedRequest.additionalFiles?.length || 0,
        documentFileName: unifiedRequest.documentFile.name,
        sessionId: unifiedRequest.sessionId || "none",
      });

      // Send through the agent-aware adapter
      await agentAwareApiAdapter.sendChatRequest(unifiedRequest, this.agentId, adapterCallbacks);
    } catch (error) {
      log.error("Agent-aware streaming failed", {
        agentId: this.agentId,
        error: error instanceof Error ? error.message : String(error),
      });

      // Preserve the original error object to keep backend messages intact
      callbacks.onError(error as Error);
    }
  }

  /**
   * Generate streaming summary - agent-aware summary endpoint
   *
   * This method handles summary generation with proper agent routing,
   * similar to how generateStreamingResponse handles chat requests.
   *
   * @deprecated RETIRED - nothing calls this. It existed solely for the
   * Analysis tab's Analyze button, and that tab has been removed from the UI.
   * The backend `/summary/*` routes it targets are unmounted, so calling it
   * now would only produce a 404. Kept because the summary prompt work behind
   * it may be worth reviving; doing so needs those routes remounted first.
   */
  async generateStreamingSummary(
    accessToken: string,
    documentFile: File,
    callbacks: {
      onStart: () => void;
      onChunk: (chunk: string) => void;
      onComplete: () => void;
      onError: (error: Error) => void;
    }
  ): Promise<AbortController> {
    try {
      log.info("Processing agent-aware summary request", {
        agentId: this.agentId,
        fileName: documentFile.name,
        fileSize: documentFile.size,
      });

      // Create unified summary request
      const details: { [key: string]: any } = {};
      if (this.contractType) {
        details.contract_type = this.contractType;
      }

      const unifiedRequest: UnifiedSummaryRequest = {
        documentFile,
        additionalFiles: this.additionalFiles,
        sessionId: nullToUndefined(this.sessionId),
        bypassCache: false,
        details,
        accessToken: accessToken,
      };

      // Send through the agent-aware adapter
      return await agentAwareApiAdapter.sendSummaryRequest(unifiedRequest, this.agentId, callbacks);
    } catch (error) {
      log.error("Agent-aware summary failed", {
        agentId: this.agentId,
        error: error instanceof Error ? error.message : String(error),
      });

      // Create an abort controller to return even on error
      const abortController = new AbortController();
      // Preserve the original error object
      callbacks.onError(error as Error);
      return abortController;
    }
  }

  /**
   * Cancel ongoing streaming request
   */
  cancelStreaming(): void {
    agentAwareApiAdapter.cancelRequest(this.agentId);
    this.resetSessionFeatures();
    this.resetThinkingState();
  }

  /**
   * Clear session - important for agent switching
   *
   * When clearing a session, we need to ensure both the local state
   * and the adapter's state are synchronized.
   */
  clearSession(): void {
    const previousSessionId = this.sessionId;
    this.sessionId = null; // Will be regenerated on next request

    // A new chat is not a resumed one, so the notice must not ride along.
    this.sessionResumePending = false;
    this.sessionResumeAttachmentNames = [];

    // Clear agent-specific session in the adapter
    agentAwareApiAdapter.clearAgentSession(this.agentId);

    // Clear local state
    this.resetSessionFeatures();
    this.resetThinkingState();

    // NOTE: We do NOT clear additional files here anymore
    // Files are managed at the application level and should persist
    // across session clears to maintain user context

    // NOTE: We do NOT clear contract type here as it persists across agent switches
    // Contract type is managed at the session level and synced when needed

    log.info("Agent session cleared", {
      agentId: this.agentId,
      previousSessionId,
      contractType: this.contractType, // Log the preserved contract type
      preservedFileCount: this.additionalFiles.length, // Log preserved files
      preservedFileNames: this.additionalFiles.map((f) => f.name),
    });
  }

  /**
   * Transform ModelRequest to UnifiedChatRequest
   *
   * This transformation is where we adapt the existing interface to our
   * new unified format. It's like translating between two languages.
   */
  private async transformToUnifiedRequest(request: ModelRequest): Promise<UnifiedChatRequest> {
    // Prepare document files (similar to ChatModelService)
    const { documentFile, selectedTextFile, docId, trackChangesMode, markupMode } =
      await this.prepareDocumentFiles(request.documentContext);

    // Transform conversation history
    const chatHistory = this.transformConversationHistory(request.conversationHistory);

    // Prepare details based on preferences
    const details = this.prepareDetails(request.preferences, trackChangesMode, markupMode);

    return {
      message: request.prompt,
      documentFile,
      additionalFiles: this.additionalFiles, // Include additional files for contract-qa
      selectedTextFile,
      sessionId: nullToUndefined(this.sessionId),
      documentId: docId,
      bypassCache: false,
      details,
      chatHistory,
      accessToken: request.accessToken, // Pass through access token if available
    };
  }

  /**
   * Prepare document files from context
   *
   * This method is similar to ChatModelService but returns files in our
   * unified format. The logic remains the same - we're just organizing
   * the output differently.
   */
  private async prepareDocumentFiles(documentContext?: {
    currentSelection?: string;
    fullDocument?: string;
    trackChangesMode?: string;
    markupMode?: string;
  }): Promise<{
    documentFile: File;
    selectedTextFile?: File;
    docId?: string;
    trackChangesMode?: string;
    // Required for the same reason as on ModelRequest: an omitted return value
    // here silently drops the display mode on the way to prepareDetails, which
    // is a bug the compiler should catch rather than a Word run.
    markupMode: string;
  }> {
    // TEMPORARY: Selected text feature disabled - allow empty selection
    // TODO: Re-enable by uncommenting the validation below
    /*
    if (!documentContext?.currentSelection) {
      throw new Error("Document context with current selection is required");
    }
    */

    // For now, we need at least the documentContext object
    if (!documentContext) {
      throw new Error("Document context is required");
    }

    // TEMPORARY: Handle empty selection - use full document if selection is empty
    const fullDocumentContent =
      documentContext.fullDocument || documentContext.currentSelection || "";
    const selectedTextContent = documentContext.currentSelection || "";

    // Generate document ID for caching
    const docId = generateDocumentId(fullDocumentContent);

    // Create file objects
    const timestamp = Date.now();
    const documentFile = createXmlFile(fullDocumentContent, `document_${timestamp}.xml`);

    // TEMPORARY: Don't create selection file when selection is empty
    const selectedTextFile =
      selectedTextContent && selectedTextContent !== fullDocumentContent
        ? createXmlFile(selectedTextContent, `selection_${timestamp}.xml`)
        : undefined;

    // Extract track changes mode from document context (if provided)
    const trackChangesMode = documentContext?.trackChangesMode;
    // Absent means the caller could not determine it, which is the same thing
    // the backend understands "unknown" to mean: keep the default behaviour.
    const markupMode = documentContext?.markupMode ?? "unknown";

    log.debug("Document files prepared", {
      agentId: this.agentId,
      documentLength: fullDocumentContent.length,
      hasSelectedText: !!selectedTextFile,
      docId,
      trackChangesMode: trackChangesMode || "not-available",
      markupMode,
    });

    return {
      documentFile,
      selectedTextFile,
      docId,
      trackChangesMode,
      markupMode,
    };
  }

  /**
   * Transform conversation history to chat message format
   * Handles both plain text and structured Anthropic content blocks (tool_use/tool_result).
   */
  private transformConversationHistory(
    conversationHistory?: { role: string; content: string | unknown[] }[]
  ): ChatMessage[] {
    if (!conversationHistory || conversationHistory.length === 0) {
      return [];
    }

    return conversationHistory.map((message) => ({
      role: message.role as "user" | "assistant",
      content: typeof message.content === "string" ? { text: message.content } : message.content,
    }));
  }

  /**
   * Prepare details configuration
   */
  private prepareDetails(preferences?: any, trackChangesMode?: string, markupMode?: string): any {
    const extendedThinking = preferences?.extendedThinking || false;
    const webSearch = preferences?.webSearch || false; // Use user preference
    const benchmarkMode = preferences?.benchmarkMode || false; // Use user preference
    const conciseMode = preferences?.conciseMode || false; // Use user preference

    const details: { [key: string]: any } = {
      thinking: extendedThinking,
      web_search: webSearch,
      web_fetch: webSearch, // Enable web fetch whenever web search is enabled
      benchmark_mode: benchmarkMode,
      concise_mode: conciseMode,
      document_citations: true,
    };

    // Include track changes mode if available
    if (trackChangesMode && trackChangesMode !== "unknown") {
      details.track_changes_mode = trackChangesMode;

      log.info("Track changes mode included in details", {
        agentId: this.agentId,
        trackChangesMode,
      });
    }

    // How Word is DISPLAYING tracked changes. The backend needs this to build
    // search phrases the frontend can actually locate: "Balloon" takes tracked
    // deletions out of the text layer that search() reads. Omitted when unknown
    // so the backend keeps its inline-deletions default.
    if (markupMode && markupMode !== "unknown") {
      details.markup_mode = markupMode;
    }
    // Include contract type if available
    if (this.contractType) {
      details.contract_type = this.contractType;
    }

    // Include selected playbook id if the user has chosen one for this session
    // (Catylex multi-playbook tenants) so the backend fetches that playbook.
    if (preferences?.selectedPlaybookId) {
      details.selected_playbook_id = preferences.selectedPlaybookId;
    }

    // Mark a playbook-selection continuation turn so the backend doesn't log the
    // synthetic continuation note as the user's question in analytics.
    if (preferences?.isPlaybookResume) {
      details.is_playbook_resume = true;
    }

    // First turn after a stored conversation was reopened. Cleared here rather
    // than after the response, because the notice belongs to this request and
    // the rest of the conversation continues normally.
    if (this.sessionResumePending) {
      details.session_resumed = true;
      if (this.sessionResumeAttachmentNames.length > 0) {
        details.resumed_attachment_names = this.sessionResumeAttachmentNames;
      }
      this.sessionResumePending = false;
      this.sessionResumeAttachmentNames = [];

      log.info("Session resume notice included in details", { agentId: this.agentId });
    }

    // Sent only for the add-in's own action. The backend withholds tools and
    // pre-loads the self-service playbook only for a turn the add-in asserted,
    // never for one inferred from typed text.
    if (preferences?.quickAnalysis) {
      details.quick_analysis = true;
      log.info("Quick analysis turn included in details", { agentId: this.agentId });
    }

    // Include document attachment for Outlook mail (from useDocumentAttachment hook)
    if (preferences?.openDocumentBytes) {
      details.open_document_bytes = preferences.openDocumentBytes;
      details.open_document_name = preferences.openDocumentName || "Document.docx";
      log.info("Document attachment included in details for Outlook mail", {
        agentId: this.agentId,
        documentName: details.open_document_name,
        bytesLength: details.open_document_bytes.length,
      });
    }

    this.currentSessionFeatures.hasThinkingEnabled = extendedThinking;
    this.currentSessionFeatures.hasWebSearchEnabled = webSearch;

    log.info("DETAILS PREPARED", {
      agentId: this.agentId,
      thinking: extendedThinking,
      webSearch: webSearch,
      webFetch: webSearch, // Log that web fetch is enabled with web search
      benchmarkMode: benchmarkMode,
      conciseMode: conciseMode,
      documentCitations: true,
      trackChangesMode: trackChangesMode || "not-detected",
      contractType: this.contractType,
      details,
    });

    return details;
  }

  /**
   * Create callback bridge between adapter and UI
   *
   * This is where we translate the streaming callbacks from the adapter
   * into the format expected by our UI components. It's like having
   * a translator at a conference - the speakers (adapter) and listeners (UI)
   * don't need to know about the translation happening in between.
   */
  private createCallbackBridge(callbacks: StreamCallbacks, preferences?: any): ChatStreamCallbacks {
    let accumulatedResponse = "";
    let stopReason: string | undefined;

    return {
      onStart: () => {
        accumulatedResponse = "";
        this.resetSessionFeatures();
        this.resetThinkingState();
        callbacks.onStart();
      },

      onTextChunk: (chunk: string) => {
        accumulatedResponse += chunk;
        callbacks.onChunk(chunk);
      },

      onThinkingChunk: (chunk: string) => {
        this.currentThinkingState.accumulatedThinking += chunk;

        log.debug("Thinking chunk received", {
          agentId: this.agentId,
          chunkLength: chunk.length,
          totalThinking: this.currentThinkingState.accumulatedThinking.length,
        });

        if (callbacks.onThinkingChunk) {
          callbacks.onThinkingChunk(chunk);
        }
      },

      onMessageStart: (aiTraceId?: string) => {
        log.debug("Message started", { agentId: this.agentId, aiTraceId });
        if (aiTraceId) {
          callbacks.onAiTraceId?.(aiTraceId);
        }
      },

      onMessageStop: (reason?: string) => {
        stopReason = reason;
        if (reason === "max_tokens") {
          log.warn("Response truncated: stop_reason=max_tokens", {
            agentId: this.agentId,
            responseLength: accumulatedResponse.length,
          });
        } else {
          log.debug("Message completed", {
            agentId: this.agentId,
            responseLength: accumulatedResponse.length,
          });
        }
      },

      onTextStart: () => {
        log.debug("Text streaming started", { agentId: this.agentId });
      },

      onTextStop: () => {
        log.debug("Text streaming completed", { agentId: this.agentId });
      },

      onThinkingStart: () => {
        // Each thinking_start produces a fresh inline UI event. Reset the
        // segment accumulator so onThinkingComplete reports just this segment.
        this.currentThinkingState.isThinking = true;
        this.currentThinkingState.thinkingStartTime = Date.now();
        this.currentThinkingState.accumulatedThinking = "";

        if (callbacks.onThinkingStart) {
          callbacks.onThinkingStart();
        }
      },

      onThinkingStop: (signature: string | null) => {
        this.currentThinkingState.isThinking = false;
        this.currentThinkingState.thinkingEndTime = Date.now();

        if (callbacks.onThinkingComplete) {
          callbacks.onThinkingComplete(this.currentThinkingState.accumulatedThinking, signature);
        }
      },

      onWebSearchToolUse: (toolInfo: any) => {
        log.info("🔍 Web search initiated", {
          agentId: this.agentId,
          toolName: toolInfo.name,
        });
        this.currentSessionFeatures.hasWebSearchEnabled = true;

        // Forward to original callback
        if (callbacks.onWebSearchToolUse) {
          callbacks.onWebSearchToolUse(toolInfo);
        }
      },

      onWebSearchQuery: (query: any) => {
        this.currentSessionFeatures.webSearchQueries.push(query.query);
        log.info("📝 Web search query", {
          agentId: this.agentId,
          query: query.query,
          queryCount: this.currentSessionFeatures.webSearchQueries.length,
        });

        // Forward to original callback
        if (callbacks.onWebSearchQuery) {
          callbacks.onWebSearchQuery(query);
        }
      },

      onWebSearchResults: (results: any[]) => {
        this.currentSessionFeatures.webSearchResults.push(...results);
        log.info("📊 Web search results received", {
          agentId: this.agentId,
          resultCount: results.length,
          totalResultsInSession: this.currentSessionFeatures.webSearchResults.length,
        });

        // Forward to original callback
        if (callbacks.onWebSearchResults) {
          callbacks.onWebSearchResults(results);
        }
      },

      onWebFetchResult: (result: any) => {
        log.info("🌐 Web fetch completed", {
          agentId: this.agentId,
          status: result.status,
          url: result.url,
          title: result.title || "No title",
          hasError: result.status === "error",
        });

        // Forward to original callback
        if (callbacks.onWebFetchResult) {
          callbacks.onWebFetchResult(result);
        }
      },

      onAutoRedlineToolResult: (result: any) => {
        log.debug("Auto-redline output received", {
          agentId: this.agentId,
          sectionName: result.sectionName,
          editsCount: result.edits?.length || 0,
        });

        // Forward to original callback
        if (callbacks.onAutoRedlineToolResult) {
          callbacks.onAutoRedlineToolResult(result);
        } else {
          log.warn("No onAutoRedlineToolResult callback registered");
        }
      },

      onAddCommentToolResult: (result: any) => {
        log.debug("Add-comment output received", {
          agentId: this.agentId,
          sectionName: result.sectionName,
          paragraphId: result.paragraphId,
          hasAnchorPhrase: !!result.anchorPhrase,
        });

        // Forward to original callback
        if (callbacks.onAddCommentToolResult) {
          callbacks.onAddCommentToolResult(result);
        } else {
          log.warn("No onAddCommentToolResult callback registered");
        }
      },

      onFormRequest: (payload: any) => {
        log.info("Form request received", {
          agentId: this.agentId,
          formType: payload.formType,
          fieldCount: payload.fields?.length || 0,
        });

        // Forward to original callback
        if (callbacks.onFormRequest) {
          callbacks.onFormRequest(payload);
        } else {
          log.warn("No onFormRequest callback registered");
        }
      },

      onPlaybookToolResult: (result: any) => {
        log.debug("Playbook tool result forwarding", {
          agentId: this.agentId,
          contentLength: result.content_length || 0,
        });

        // Forward to original callback
        if (callbacks.onPlaybookToolResult) {
          callbacks.onPlaybookToolResult(result);
        } else {
          log.warn("No onPlaybookToolResult callback registered");
        }
      },

      onPlaybookSelectionRequest: (result: any) => {
        log.debug("Playbook selection card forwarding", {
          agentId: this.agentId,
          playbookCount: result.playbooks?.length || 0,
        });

        // Forward to original callback
        if (callbacks.onPlaybookSelectionRequest) {
          callbacks.onPlaybookSelectionRequest(result);
        } else {
          log.warn("No onPlaybookSelectionRequest callback registered");
        }
      },

      onCatylexSearchResult: (result: NormalizedCatylexSearchResult) => {
        log.debug("Catylex search result forwarding", {
          agentId: this.agentId,
          status: result.status,
          searchType: result.searchType,
          totalResults: result.totalResults,
        });

        if (callbacks.onCatylexSearchResult) {
          callbacks.onCatylexSearchResult(result);
        } else {
          log.warn("No onCatylexSearchResult callback registered");
        }
      },

      onCatylexToolInput: (input: CatylexToolInput) => {
        log.debug("Catylex tool input detected", { action: input.action });
        if (callbacks.onCatylexToolInput) {
          callbacks.onCatylexToolInput(input);
        }
      },

      onSharePointToolInput: (input: SharePointToolInput) => {
        log.debug("SharePoint tool input detected", { action: input.action });
        if (callbacks.onSharePointToolInput) {
          callbacks.onSharePointToolInput(input);
        }
      },

      onSharePointSearchResult: (result: NormalizedSharePointResult) => {
        log.debug("SharePoint search result forwarding", {
          agentId: this.agentId,
          action: result.action,
          status: result.status,
        });

        if (callbacks.onSharePointSearchResult) {
          callbacks.onSharePointSearchResult(result);
        } else {
          log.warn("No onSharePointSearchResult callback registered");
        }
      },

      onOutlookMailToolInput: (input: OutlookMailToolInput) => {
        log.debug("Outlook mail tool input detected", { action: input.action });
        if (callbacks.onOutlookMailToolInput) {
          callbacks.onOutlookMailToolInput(input);
        }
      },

      onOutlookMailResult: (result: OutlookMailResult) => {
        log.debug("Outlook mail result forwarding", {
          agentId: this.agentId,
          action: result.action,
          status: result.status,
        });

        if (callbacks.onOutlookMailResult) {
          callbacks.onOutlookMailResult(result);
        } else {
          log.warn("No onOutlookMailResult callback registered");
        }
      },

      onDocumentCitation: (citation: DocumentCitation) => {
        log.debug("Document citation received", {
          agentId: this.agentId,
          ref: citation.ref,
          documentTitle: citation.document_title,
        });

        // Forward to original callback
        if (callbacks.onDocumentCitation) {
          callbacks.onDocumentCitation(citation);
        }
      },

      onWebCitations: (insertions: WebCitationInsertion[]) => {
        callbacks.onWebCitations?.(insertions);
      },

      onToolUseStart: (toolName: string) => {
        log.debug("Tool use started", {
          agentId: this.agentId,
          toolName,
        });

        if (callbacks.onToolUseStart) {
          callbacks.onToolUseStart(toolName);
        }
      },

      onError: (error: ChatApiError) => {
        log.error("Streaming error", {
          agentId: this.agentId,
          errorCode: error.errorCode,
          errorMessage: error.message,
        });

        this.resetThinkingState();
        const bridgedError = new Error(error.message) as Error & {
          errorCode?: string;
          statusCode?: number;
        };
        bridgedError.errorCode = error.errorCode;
        // Carry the HTTP status across the bridge - analytics reads it for
        // chat:response_failed.status_code.
        bridgedError.statusCode = error.statusCode;
        callbacks.onError(bridgedError);
      },

      onComplete: () => {
        const modelResponse: ModelResponse = {
          content: accumulatedResponse,
          metadata: {
            sessionId: nullToUndefined(this.sessionId),
            agentId: this.agentId,
            thinkingContent: this.currentThinkingState.accumulatedThinking,
            thinkingDuration: this.getThinkingDuration(),
            hasAdditionalFiles: this.additionalFiles.length > 0,
            stopReason,
          },
        };

        log.info("Streaming completed", {
          agentId: this.agentId,
          responseLength: accumulatedResponse.length,
          hasThinking: this.currentThinkingState.accumulatedThinking.length > 0,
          hasWebSearch: this.currentSessionFeatures.hasWebSearchEnabled,
          webSearchQueries: this.currentSessionFeatures.webSearchQueries.length,
          webSearchResults: this.currentSessionFeatures.webSearchResults.length,
          additionalFilesUsed: this.additionalFiles.length,
        });

        callbacks.onComplete(modelResponse);
      },
    };
  }

  /**
   * Helper methods for state management
   */
  private resetSessionFeatures(): void {
    this.currentSessionFeatures = {
      hasThinkingEnabled: false,
      hasWebSearchEnabled: false,
      webSearchQueries: [],
      webSearchResults: [],
    };
  }

  private resetThinkingState(): void {
    this.currentThinkingState = {
      isThinking: false,
      thinkingStartTime: 0,
      thinkingEndTime: 0,
      accumulatedThinking: "",
    };
  }

  private getThinkingDuration(): number {
    if (this.currentThinkingState.thinkingStartTime === 0) {
      return 0;
    }

    if (this.currentThinkingState.isThinking) {
      // Still thinking - return current duration
      return Date.now() - this.currentThinkingState.thinkingStartTime;
    } else if (this.currentThinkingState.thinkingEndTime > 0) {
      // Thinking completed - use stored end time for accurate duration
      return (
        this.currentThinkingState.thinkingEndTime - this.currentThinkingState.thinkingStartTime
      );
    }

    return 0;
  }
}
